import React, { useState } from 'react';
import {
  Box,
  Button,
  Heading,
  Text,
  Progress,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  HStack,
  Badge,
  Code,
  Divider,
  useColorModeValue,
  useToast
} from '@chakra-ui/react';
import { migrateSitesToBeta } from '../../firebase/migrations/migrateSitesToBeta';

/**
 * Administrative tool to migrate sites from the standard collection 
 * to the optimized sitesearch_beta collection for 1000+ sites
 */
const MigrationTool = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [lastProcessedItem, setLastProcessedItem] = useState(null);
  const toast = useToast();

  const bg = useColorModeValue('bg.surface', 'bg.surface');
  const borderColor = useColorModeValue('border.default', 'border.default');
  
  const runMigration = async () => {
    setIsRunning(true);
    setStatus('running');
    setMessage('Preparing migration...');
    setProgress(0);
    setLogs(prev => [...prev, { 
      time: new Date().toISOString(), 
      message: 'Starting migration',
      type: 'info'
    }]);
    
    try {
      // Run the migration with a status callback
      await migrateSitesToBeta(50, (status) => {
        // Update the UI with progress
        if (status.stats) {
          setStats(status.stats);
          
          if (status.stats.total > 0) {
            setProgress((status.stats.processed / status.stats.total) * 100);
          }
        }
        
        setMessage(status.message || '');
        
        // Add to log
        setLogs(prev => [...prev, { 
          time: new Date().toISOString(), 
          message: status.message || 'Update',
          type: status.error ? 'error' : 'info'
        }]);
        
        // Update last processed item
        if (status.lastProcessed) {
          setLastProcessedItem(status.lastProcessed);
        }
      });
      
      // Success
      setStatus('completed');
      setMessage('Migration completed successfully');
      setLogs(prev => [...prev, { 
        time: new Date().toISOString(), 
        message: 'Migration completed successfully',
        type: 'success'
      }]);
      
      toast({
        title: 'Migration completed',
        description: `Successfully migrated ${stats?.success || 0} sites to the beta search collection`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      // Error
      setStatus('error');
      setMessage(`Migration failed: ${error.message}`);
      setLogs(prev => [...prev, { 
        time: new Date().toISOString(), 
        message: `Error: ${error.message}`,
        type: 'error'
      }]);
      
      toast({
        title: 'Migration failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      
      console.error('Migration error:', error);
    } finally {
      setIsRunning(false);
    }
  };
  
  return (
    <Box
      p={6}
      borderWidth="1px"
      borderRadius="lg"
      borderColor={borderColor}
      bg={bg}
      position="relative"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        opacity: 0.05,
        pointerEvents: 'none',
        bgImage: useColorModeValue('var(--texture-parchment)', 'var(--texture-leather)'),
        bgRepeat: 'repeat',
        bgSize: '300px',
        borderRadius: 'lg',
        zIndex: 0
      }}
    >
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between">
          <Heading size="lg" fontFamily="heading">1000+ Sites Migration Tool</Heading>
          <Badge
            colorScheme={
              status === 'completed' ? 'green' :
              status === 'running' ? 'blue' :
              status === 'error' ? 'red' :
              'gray'
            }
            fontSize="md"
            py={1}
            px={2}
            borderRadius="md"
          >
            {status === 'idle' ? 'Ready' : 
             status === 'running' ? 'Running' :
             status === 'completed' ? 'Completed' :
             status === 'error' ? 'Failed' : 'Unknown'}
          </Badge>
        </HStack>
        
        <Text color="text.muted" fontStyle="italic">
          This tool migrates sites from the standard 'sites' collection to the optimized 'sitesearch_beta'
          collection with enhanced data structures for efficient filtering and rendering of 1000+ sites.
        </Text>
        
        <Box>
          <Text fontWeight="medium" mb={2}>Status</Text>
          <Alert 
            status={
              status === 'completed' ? 'success' :
              status === 'running' ? 'info' :
              status === 'error' ? 'error' :
              'info'
            }
            borderRadius="md"
          >
            <AlertIcon />
            {message || "Ready to migrate sites to the optimized format."}
          </Alert>
        </Box>
        
        {isRunning && (
          <Box>
            <Text fontWeight="medium" mb={2}>Progress</Text>
            <Progress 
              value={progress} 
              size="lg" 
              colorScheme="ancient" 
              borderRadius="md" 
              hasStripe 
              isAnimated 
            />
            <HStack justify="space-between" mt={1}>
              <Text fontSize="sm" color="text.muted">
                {stats?.processed || 0}/{stats?.total || '?'} sites
              </Text>
              <Text fontSize="sm" fontWeight="medium">
                {progress.toFixed(1)}%
              </Text>
            </HStack>
          </Box>
        )}
        
        {stats && (
          <Box>
            <Text fontWeight="medium" mb={2}>Stats</Text>
            <Table size="sm" variant="simple" borderWidth="1px" borderRadius="md">
              <Thead>
                <Tr>
                  <Th>Metric</Th>
                  <Th isNumeric>Count</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>Total Sites</Td>
                  <Td isNumeric>{stats.total}</Td>
                </Tr>
                <Tr>
                  <Td>Processed</Td>
                  <Td isNumeric>{stats.processed}</Td>
                </Tr>
                <Tr>
                  <Td>Success</Td>
                  <Td isNumeric color="green.500">{stats.success}</Td>
                </Tr>
                <Tr>
                  <Td>Errors</Td>
                  <Td isNumeric color="red.500">{stats.error}</Td>
                </Tr>
                <Tr>
                  <Td>Skipped</Td>
                  <Td isNumeric>{stats.skipped}</Td>
                </Tr>
                <Tr>
                  <Td>Media Items Processed</Td>
                  <Td isNumeric>{stats.mediaProcessed}</Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        )}
        
        {lastProcessedItem && (
          <Box>
            <Text fontWeight="medium" mb={2}>Last Processed Site</Text>
            <Code p={3} borderRadius="md" width="100%" display="block">
              {lastProcessedItem.id}: {lastProcessedItem.name}
            </Code>
          </Box>
        )}
        
        <Divider />
        
        <Box>
          <Text fontWeight="medium" mb={2}>Migration Logs</Text>
          <Box
            height="200px"
            overflowY="auto"
            borderWidth="1px"
            borderRadius="md"
            p={3}
            bg={useColorModeValue('blackAlpha.50', 'whiteAlpha.50')}
            fontFamily="mono"
            fontSize="sm"
          >
            {logs.map((log, index) => (
              <Text 
                key={index}
                color={
                  log.type === 'error' ? 'red.500' :
                  log.type === 'success' ? 'green.500' :
                  log.type === 'warning' ? 'orange.500' :
                  'inherit'
                }
                mb={1}
              >
                [{log.time.slice(11, 19)}] {log.message}
              </Text>
            ))}
          </Box>
        </Box>
        
        <Box>
          <Button
            colorScheme="ancient"
            isLoading={isRunning}
            loadingText="Migrating..."
            onClick={runMigration}
            isDisabled={isRunning}
            width="100%"
            size="lg"
            variant="journal"
          >
            {status === 'completed' ? 'Run Migration Again' : 'Start Migration Process'}
          </Button>
        </Box>
      </VStack>
    </Box>
  );
};

export default MigrationTool;
